import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Package, Tags, Boxes, ShoppingCart, Users, Ticket, Star, BarChart3, Truck, Settings, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["", "Dashboard", LayoutDashboard],
  ["products", "Products", Package],
  ["categories", "Categories", Tags],
  ["inventory", "Inventory", Boxes],
  ["orders", "Orders", ShoppingCart],
  ["customers", "Customers", Users],
  ["coupons", "Coupons", Ticket],
  ["reviews", "Reviews", Star],
  ["analytics", "Analytics", BarChart3],
  ["shipping", "Shipping", Truck],
  ["settings", "Settings", Settings],
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]"><div className="w-8 h-8 border-2 border-[#FF2E4C] border-t-transparent rounded-full animate-spin" /></div>;
  if (user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex">
      <aside className="w-16 lg:w-60 shrink-0 border-r border-zinc-800 flex flex-col sticky top-0 h-screen">
        <div className="p-4 lg:p-6 border-b border-zinc-800">
          <Link to="/admin" className="font-display text-lg lg:text-xl font-extrabold tracking-tight block">
            <span className="hidden lg:inline">SKG<span className="text-[#FF2E4C]">WEAR</span></span>
            <span className="lg:hidden">S<span className="text-[#FF2E4C]">W</span></span>
          </Link>
          <p className="hidden lg:block text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-1">Admin Panel</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 no-scrollbar">
          {NAV.map(([path, label, Icon]) => (
            <NavLink key={path} to={path === "" ? "/admin" : `/admin/${path}`} end={path === ""}
              data-testid={`admin-sidebar-nav-${label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 lg:px-6 py-3 text-sm font-medium transition-colors ${isActive ? "text-white bg-[#FF2E4C]/10 border-r-2 border-[#FF2E4C]" : "text-zinc-500 hover:text-white"}`}>
              <Icon className="w-5 h-5 shrink-0" /><span className="hidden lg:inline">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-3 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-2 lg:px-3 py-2.5 text-sm text-zinc-500 hover:text-white"><ExternalLink className="w-5 h-5 shrink-0" /><span className="hidden lg:inline">Προβολή Store</span></Link>
          <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-3 px-2 lg:px-3 py-2.5 text-sm text-zinc-500 hover:text-[#FF2E4C] w-full" data-testid="admin-logout"><LogOut className="w-5 h-5 shrink-0" /><span className="hidden lg:inline">Αποσύνδεση</span></button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden"><Outlet /></main>
    </div>
  );
}

export function AdminHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
      <div>
        {subtitle && <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-1">{subtitle}</p>}
        <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">{title}</h1>
      </div>
      {action}
    </div>
  );
}
